/**
 * Messenger v2 — новый дизайн чата.
 *
 * Архитектура и логика — те же что у /chat (использует тот же ChatPage).
 * Меняется ТОЛЬКО CSS через scope-обёртку `.chat-redesign`.
 * Стили в `styles/chat-redesign.css` каскадируют поверх `chat.css`.
 */
import { useEffect, useState } from "react";
import { ChatPage } from "@/pages/ChatPage";
import { VoicePlayerProvider } from "@/contexts/VoicePlayerContext";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { useAuth } from "@/auth/AuthContext";
import { ProfileSelfModal } from "@/components/ProfileSelfModal";
import { OrgsOnboarding } from "@/components/OrgsPanel";
import "@/styles/chat-redesign.css";
import "@/styles/messenger-mobile.css";
import "@/styles/drive.css";
import "@/styles/tasks.css";
import "@/styles/brixchat.css";

export function MessengerPage() {
  const { user } = useAuth();
  // Пуш «Заявка в организацию» ведёт на /messenger?orgs=1 — профиль на вкладке «Организации» (03.10.2026).
  const [orgsOpen, setOrgsOpen] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("orgs") === "1",
  );
  const closeOrgs = () => {
    setOrgsOpen(false);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("orgs");
      window.history.replaceState(window.history.state, "", u.toString());
    } catch { /* ignore */ }
  };

  // ВАЖНО: 45+ CSS-правил в messenger-mobile.css и chat-redesign.css имеют
  // префикс body[data-app-mode="chat"]. Без этого атрибута — поиск и
  // composer выглядят сломанно (баг 2026-05-17).
  useEffect(() => {
    const prev = document.body.getAttribute("data-app-mode");
    document.body.setAttribute("data-app-mode", "chat");
    return () => {
      if (prev !== null) document.body.setAttribute("data-app-mode", prev);
      else document.body.removeAttribute("data-app-mode");
    };
  }, []);

  // Performance class detection (P2#11): на low-end девайсах (старые
  // Android, <4 cores, <2GB RAM) backdrop-filter в context-menu/dim-layer
  // лагает 1-3с — заменяем на сплошной тёмный overlay без blur.
  // Детект: hardwareConcurrency, deviceMemory + явный legacy-Android UA.
  // Класс body[data-perf="low"] → CSS правила выключают filter/backdrop-filter.
  useEffect(() => {
    const nav = navigator as Navigator & { deviceMemory?: number };
    const cores = nav.hardwareConcurrency ?? 8;
    const mem = nav.deviceMemory ?? 8;
    const ua = navigator.userAgent || "";
    const androidMatch = ua.match(/Android (\d+)/);
    const isLegacyAndroid = androidMatch ? parseInt(androidMatch[1], 10) < 9 : false;
    const isLowEnd = cores < 4 || mem < 2 || isLegacyAndroid;
    if (isLowEnd) {
      document.body.setAttribute("data-perf", "low");
    } else {
      document.body.removeAttribute("data-perf");
    }
    return () => {
      document.body.removeAttribute("data-perf");
    };
  }, []);

  // iOS Safari auto-зумит viewport при focus на input/textarea с font-size<16px.
  // font-size раздувать нельзя (ломает дизайн). Паттерн как в PhotoLightbox:
  // на focus меняем meta viewport на maximum-scale=1+user-scalable=no, на blur
  // возвращаем. Делаем delegation на document, чтобы покрыть и composer, и
  // search-input, и emoji-picker — всё что фокусится в чате.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute("content") || "";
    const noZoom = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable) {
        meta.setAttribute("content", noZoom);
      }
    };
    const onBlur = () => meta.setAttribute("content", original);
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("focusout", onBlur, true);
    return () => {
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", onBlur, true);
      meta.setAttribute("content", original);
    };
  }, []);

  return (
    <VoicePlayerProvider>
      <WorkspaceSwitcher/>
      <div className="chat-redesign">
        <ChatPage/>
      </div>
      {user && orgsOpen && <ProfileSelfModal initialTab="orgs" onClose={closeOrgs}/>}
      {!orgsOpen && <OrgsOnboarding/>}
    </VoicePlayerProvider>
  );
}
